import { v4 as uuidv4 } from 'uuid';
import MXCache from 'Common/CacheManager/MXCache';
import { IAPM, CoralogixRumSdk, CoralogixSeverity } from './apm.types';
import { LogActionType } from './apm.enum';
import {
    getNetworkConnectionType,
    getDeviceInfo,
    getCurrentPageId,
} from './enrichment';

const Severity = {
    DEBUG: 1 as CoralogixSeverity,
    VERBOSE: 2 as CoralogixSeverity,
    INFO: 3 as CoralogixSeverity,
    WARN: 4 as CoralogixSeverity,
    ERROR: 5 as CoralogixSeverity,
    CRITICAL: 6 as CoralogixSeverity,
};

// True only once the CDN script has actually loaded and the global has been
// replaced with the real SDK object (has `.log` etc.).
function hasLoadedSdk(): boolean {
    const rum = window.CoralogixRum as any;
    return !!rum && typeof rum.log === 'function';
}

// True while the queue stub is in place (a function with a `.q` array).
function hasQueueStub(): boolean {
    const rum = window.CoralogixRum as any;
    return !!rum && typeof rum === 'function' && Array.isArray(rum.q);
}

// Call an SDK method, falling back to the pre-load queue when the script
// hasn't loaded yet. The Coralogix loader stub replays queued calls once
// the SDK initializes.
function callOrQueue(method: keyof CoralogixRumSdk, args: any[]): void {
    if (hasLoadedSdk()) {
        const sdk = window.CoralogixRum as CoralogixRumSdk;
        (sdk[method] as any)?.(...args);
        return;
    }
    if (hasQueueStub()) {
        const stub = window.CoralogixRum as (...args: any[]) => void;
        stub(method, ...args);
    }
    // else: SDK was never loaded (e.g. CDN blocked) — silently drop.
}

export function createCoralogixAPM(): IAPM {
    // Per-tab session id used to correlate events for the Session Load
    // Success Rate metric and to scope all other metrics to a session.
    const sessionId = uuidv4();

    // Local mirror of all custom attributes set via setCustomAttribute.
    // Re-applied to Coralogix as global labels.
    const globalAttributes: Record<string, string | number | boolean> = {
        sessionId,
    };

    // Push the sessionId to the SDK so any future auto-instrumented events
    // (should those ever be enabled) also carry it.
    callOrQueue('setAttribute', ['sessionId', sessionId]);

    return {
        noticeError(
            error: Error | string,
            customAttributes?: Record<string, any>
        ): void {
            try {
                const errorPayload =
                    typeof error === 'string' ? new Error(error) : error;
                callOrQueue('error', [
                    errorPayload,
                    customAttributes,
                    { ...globalAttributes },
                ]);
            } catch (e) {
                console.error('[APM] Error in noticeError:', e);
            }
        },

        addPageAction(
            actionName: string,
            attributes?: Record<string, any>
        ): void {
            try {
                callOrQueue('log', [
                    Severity.INFO,
                    actionName,
                    {
                        ...attributes,
                        'action.name': actionName,
                    },
                    { ...globalAttributes },
                ]);
            } catch (e) {
                console.error('[APM] Error in addPageAction:', e);
            }
        },

        setCustomAttribute(
            key: string,
            value: string | number | boolean
        ): void {
            globalAttributes[key] = value;
            try {
                callOrQueue('setAttribute', [key, String(value)]);

                // For user identity, set the internal user_id only.
                // Do NOT pass email/name — they are PII and should not flow
                // into RUM events.
                if (key === 'userId') {
                    callOrQueue('setUserContext', [
                        {
                            userId: String(value),
                            userMetadata: { ...globalAttributes },
                        },
                    ]);
                }
            } catch (e) {
                console.error('[APM] Error in setCustomAttribute:', e);
            }
        },

        logAction(
            actionName: string,
            additionalPayload: Record<string, any> = {}
        ): void {
            try {
                const tenantDetails = MXCache.GetCacheItem(
                    'LSQ_CONVERSE_TenantDetails'
                );
                const user = MXCache.GetCacheItem('LSQ_CONVERSE_UserFields');

                const enrichedPayload = {
                    ...additionalPayload,
                    tenantId: tenantDetails?.orgCode,
                    userId: user?.userId,
                    regionId: tenantDetails?.regionId,
                    ...getNetworkConnectionType(),
                    ...getDeviceInfo(),
                    pageId: getCurrentPageId(),
                };

                this.addPageAction(actionName, enrichedPayload);
            } catch (e) {
                console.error('[APM] Error in logAction:', e);
            }
        },

        registerBtnClick(
            buttonIdentifier: string,
            metadata?: Record<string, any>
        ): void {
            this.logAction(LogActionType.BTN_CLICK, {
                buttonString: buttonIdentifier,
                ...metadata,
            });
        },

        shutdown(): void {
            try {
                if (hasLoadedSdk()) {
                    const sdk = window.CoralogixRum as CoralogixRumSdk;
                    sdk.shutdown?.();
                }
            } catch (e) {
                console.error('[APM] Error during shutdown:', e);
            }
        },
    };
}
